/**
 * Series colours for the clinic dashboard's charts.
 *
 * **These are not brand tokens** -- those live in mixins.ts and still dress every button,
 * heading and accent on the page. Series colour is data encoding rather than branding, and
 * the two brand hues cannot do this job: there are only two of them where up to eight
 * slots are needed, and tinting them to fill the gap invents hues nobody checked.
 *
 * That was true when the two hues were fixed. It is more true now that they are chosen per
 * clinic: a palette derived from them would be unreviewed *and* different at every clinic,
 * so no discriminability check could ever have been run on what a given user is looking at.
 * The old brand gold sat near 1.7:1 against white, which works on a line chart because
 * stroke weight and dashes carry the distinction too -- but in a grouped bar chart colour
 * is the only channel there is, and a clinic is free to pick something paler still.
 *
 * This order was validated for discriminability on a white surface: every hue inside the
 * lightness band and above the chroma floor, worst adjacent pair 9.1 ΔE under simulated
 * colour-vision deficiency and 19.6 to normal vision. Three of them fall below 3:1
 * contrast, which obliges relief -- the charts print the value above every bar and the
 * therapist table repeats every figure as text, so that obligation is met twice.
 *
 * The first slot happens to sit within a couple of ΔE of `brandColors.blue`, so a total
 * still reads as the product's colour.
 */
export const CHART_SERIES_COLORS = [
    '#2a78d6', // blue
    '#eb6834', // orange
    '#1baf7a', // aqua
    '#eda100', // yellow
    '#e87ba4', // magenta
    '#008300', // green
    '#4a3aa7', // violet
    '#e34948', // red
] as const;

/** Slot 0 belongs to a total in both charts, so a total always looks like a total. */
export const TOTAL_SERIES_COLOR: string = CHART_SERIES_COLORS[0];
export const NEW_SERIES_COLOR: string = CHART_SERIES_COLORS[1];
export const EXISTING_SERIES_COLOR: string = CHART_SERIES_COLORS[2];

/** Slot 0 is reserved for Total, leaving seven for people. */
export const THERAPIST_SLOTS: number = CHART_SERIES_COLORS.length - 1;

/**
 * A therapist's colour, from their position in the API's `therapistTrend`.
 *
 * That list is ordered by therapist id with the unassigned bucket last -- a stable
 * identity, deliberately not a rank -- which is the whole point: hiding a colleague or
 * switching between billed and earned must not repaint anybody. Colour follows the person,
 * never their position in a league table.
 *
 * An eighth therapist wraps to the first slot rather than getting a computed hue. Two
 * series sharing a colour is honest and the legend disambiguates, whereas an
 * algorithmically-derived eighth hue would land somewhere unvalidated. Past seven people
 * colour cannot separate them whatever the palette, and the answer is a filter rather than
 * more hues.
 */
export const therapistSeriesColor = (index: number): string =>
    CHART_SERIES_COLORS[(index % THERAPIST_SLOTS) + 1];
